import { ApiService } from "@/core/shared/services/ApiService";

import type { AxiosProgressEvent, GenericAbortSignal } from "axios";

import type {
  CampaignCalculationBlastJobs,
  CampaignCreated,
  CampaignCreationFormData,
  CampaignEditionDraft,
  CampaignInviteLimits,
  CampaignSourceFile,
  CampaignStats,
  CampaignUpdated,
  ExportTiktokJobsDownload,
  JobCalculationData,
  SourceFilesDownload,
} from "@/modules/campaign/services/CampaignService.types";
import type { CreditCardUpdated } from "@/shared/services/CreditCardService.types.ts";
import type { CampaignExtended } from "@/shared/services/SearchService.types";

import type { UgcTypeName } from "@/shared/enums/UgcTypeEnum";

export default class CampaignService {
  /**
   * @param data
   * @param ugcType
   */
  static create(
    data: CampaignCreationFormData,
    ugcType: UgcTypeName
  ): Promise<CampaignCreated> {
    return ApiService.post(`/company/campaigns/${ugcType}`, data);
  }

  /**
   * @param campaignId
   */
  static get(campaignId: number): Promise<CampaignExtended> {
    return ApiService.get(`/company/campaigns/${campaignId}`);
  }

  /**
   * @param campaignId
   */
  static getDraft(campaignId: number): Promise<CampaignEditionDraft> {
    return ApiService.get(`/company/campaigns/${campaignId}/drafts`);
  }

  /**
   * @param data
   * @param campaignId
   */
  static updateDraft(
    campaignId: number,
    data: FormData
  ): Promise<CampaignExtended> {
    return ApiService.post(`/company/campaigns/${campaignId}/drafts`, data);
  }

  /**
   * @param data
   */
  static getJobCalculation(data: FormData): Promise<JobCalculationData> {
    return ApiService.post("/company/campaigns/cost-calculation", data);
  }

  /**
   * @param data
   */
  static jobCalculation(data: FormData): Promise<JobCalculationData> {
    return ApiService.post("/company/campaigns/new-cost-calculation", data);
  }

  /**
   *
   * @param campaignId
   */
  static pause(campaignId: number): Promise<CampaignUpdated> {
    return ApiService.put(`/company/campaigns/${campaignId}/pause`);
  }

  /**
   *
   * @param campaignId
   */
  static unpause(campaignId: number): Promise<CampaignUpdated> {
    return ApiService.put(`/company/campaigns/${campaignId}/unpause`);
  }

  /**
   * @endpoint /campaign/campaign_id/stats
   * @httpMethod GET
   * @param campaignId
   */
  static stats(campaignId: number): Promise<CampaignStats> {
    return ApiService.get(`/company/campaigns/${campaignId}/stats`);
  }

  static addJobs(
    campaignId: number,
    maxJobs: number
  ): Promise<CampaignUpdated> {
    return ApiService.put(`/company/campaigns/${campaignId}/max-jobs`, {
      max_jobs: maxJobs,
    });
  }

  static addBlastJobs(
    campaignId: number,
    creditCardId: number,
    nrJobs: number
  ): Promise<CampaignUpdated> {
    return ApiService.put(
      `/company/campaigns/${campaignId}/increase-jobs-to-bundle-campaign`,
      {
        credit_card_id: creditCardId,
        nr_of_jobs: nrJobs,
      }
    );
  }

  static calculateBlastJobs(
    campaignId: number,
    nrJobs: number
  ): Promise<CampaignCalculationBlastJobs> {
    return ApiService.post(
      `/company/campaigns/${campaignId}/calculated-increase-bundle-jobs`,
      {
        nr_of_jobs: nrJobs,
      }
    );
  }

  /**
   * @endpoint /campaign/campaign_id/creator-limits
   * @httpMethod GET
   * @param campaignId
   */
  static getInviteLimits(campaignId: number): Promise<CampaignInviteLimits> {
    return ApiService.get(`/company/campaigns/${campaignId}/creator-limits`);
  }

  /**
   * @endpoint /company/campaigns/upload-source-file
   * @httpMethod POST
   * @param data FormData
   * @param onUploadProgress AxiosProgressEvent
   * @param signal
   */
  static addSourceFile(
    data: FormData,
    onUploadProgress?: (event: AxiosProgressEvent) => void,
    signal?: GenericAbortSignal
  ): Promise<CampaignSourceFile> {
    return ApiService.post(
      "/company/campaigns/upload-source-file",
      data,
      undefined,
      undefined,
      onUploadProgress,
      signal
    );
  }

  /**
   * @endpoint /company/export/tiktok-jobs/
   * @httpMethod POST
   * @param campaignId
   */
  static exportTiktokJobs(
    campaignId: number
  ): Promise<ExportTiktokJobsDownload> {
    return ApiService.post("/company/export/tiktok-jobs", {
      campaign_id: campaignId,
    });
  }

  /**
   * @endpoint /company/campaigns/id/delete-source-file
   * @httpMethod DELETE
   * @param id
   */
  static deleteSourceFile(id: number): Promise<CampaignUpdated> {
    return ApiService.delete(`/company/campaigns/${id}/delete-source-file`);
  }

  /**
   * Charges a card
   * @throws {ApiServiceError}
   * @return {Promise<CreditCardUpdated>}
   */
  static updatePaymentMethod(
    campaignId: number,
    creditCardId: number
  ): Promise<CreditCardUpdated> {
    return ApiService.put(`/company/campaigns/${campaignId}/charge-settings`, {
      credit_card_id: creditCardId,
      use_company_wallet: 1,
    });
  }

  /**
   * @endpoint /company/campaigns/id/delete-source-file
   * @httpMethod DELETE
   */
  static deleteAllSourceFiles(): Promise<CampaignUpdated> {
    return ApiService.delete(`/company/campaigns/delete-source-files`);
  }

  /**
   * @endpoint /company/campaigns/${campaignId}/change-daily-jobs
   * @httpMethod POST
   * @param campaignId
   * @param dailyJobs
   */
  static updateDailyJobs(
    campaignId: number,
    dailyJobs: number
  ): Promise<ExportTiktokJobsDownload> {
    return ApiService.post(
      `/company/campaigns/${campaignId}/change-daily-jobs`,
      {
        max_daily_jobs: dailyJobs,
      }
    );
  }

  /**
   * @endpoint /company/campaigns/${id}/download-source-file
   * @httpMethod GET
   * @param fileId
   */
  static downloadSourceFile(fileId: number): Promise<SourceFilesDownload> {
    return ApiService.get(`/company/campaigns/${fileId}/download-source-file`);
  }

  /**
   * @endpoint /company/campaigns/download-source-files/${campaignId}
   * @httpMethod GET
   * @param campaignId
   */
  static downloadSourceFiles(campaignId: number): Promise<SourceFilesDownload> {
    return ApiService.get(
      `/company/campaigns/${campaignId}/download-source-files`
    );
  }
}
